<template>
	<FormCard class="">
		<h3>Material Composition Breakdown - per {{ calculationOptions?.outputQuantity }} {{ selectedUnit?.unitName }} of {{ outputDescription }}</h3>
		<ResultGridFilterBadge :visible-count="visibleRows.length" :total-count="resultsByMaterialCompositionList.length" :is-filtered="isFiltered" @clear="clearAllFilters" />
		<div class="flex">
			<ag-grid-vue
				:theme="resultGridTheme"
				style="width: 100%; min-height: 350px"
				:rowData="resultsByMaterialCompositionList"
				:columnDefs="agGridColumnDefs"
				:loadingOverlayComponentParams="{ loadingMessage: 'Loading result by step. Please wait.' }"
				:noRowsOverlayComponentParams="{ noRowsMessageFunc: () => 'No result by step found.' }"
				:overlayLoadingTemplate="'<span class=&quot;ag-overlay-loading-center&quot;>Loading result by step. Please wait.</span>'"
				:overlayNoRowsTemplate="'<span class=&quot;ag-overlay-no-rows-center&quot;>No result by step found.</span>'"
				:context="{}"
				:loadThemeGoogleFonts="true"
				:defaultColDef="resultGridDefaultColDef"
				:pinnedBottomRowData="pinnedBottomRow"
				@grid-ready="onGridReady"
				@row-data-updated="refreshVisibleRows"
				@model-updated="refreshVisibleRows"
				@filter-changed="refreshVisibleRows"
				@sort-changed="refreshVisibleRows"
				@first-data-rendered="refreshVisibleRows"
			>
			</ag-grid-vue>
		</div>

		<!-- Chart Type Toggle -->
		<div class="flex mt-2 mb-2 justify-center">
			<div class="flex items-center space-x-4">
				<span class="text-sm font-medium" :class="{ 'text-blue-600': !showPieChart, 'text-gray-500': showPieChart }">Bar Chart</span>
				<label class="relative inline-flex items-center cursor-pointer">
					<input type="checkbox" v-model="showPieChart" class="sr-only peer" />
					<div
						class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
					></div>
				</label>
				<span class="text-sm font-medium" :class="{ 'text-blue-600': showPieChart, 'text-gray-500': !showPieChart }">Pie Chart</span>
				<label v-show="!showPieChart" v-tooltip.top="'Display charts in a scrollable container with fixed height. Useful when there are many items.'" class="flex items-center gap-1 text-sm text-gray-500 cursor-pointer select-none ml-4">
					<input type="checkbox" v-model="scrollMode" class="accent-blue-600" />
					Scroll
				</label>
				<label v-show="!showPieChart" v-tooltip.top="'Widen each chart (fewer charts per row) for a clearer visualization.'" class="flex items-center gap-1 text-sm text-gray-500 cursor-pointer select-none ml-2">
					<input type="checkbox" v-model="extendMode" class="accent-blue-600" />
					Extend
				</label>
			</div>
		</div>
		<!-- Dynamic Charts Component with CSS Grid Layout -->
		<div :class="['grid gap-4 mt-4', extendMode ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3']">
			<DynamicChartComponent
				v-for="emissionFactor in activeEmissionFactors"
				:key="emissionFactor.id"
				:title="
					props.calculationOptions?.useWeightedEmissionFactors
						? emissionFactor.emissionFactorName?.replace(/ \[.*\]$/, ' [mPt]')
						: emissionFactor.emissionFactorName
				"
				:emissionFactors="emissionFactor"
				:chartData="chartDataItems"
				:showPieChart="showPieChart"
				:scrollMode="scrollMode"
				class="min-h-96 flex flex-col"
			/>
		</div>
	</FormCard>
</template>
<script setup lang="ts">
import { ref, onMounted, computed, watch, PropType } from "vue"
import {
	ApiClient,
	MgmRawMaterialsClient,
	DataQualityRating,
	CalculationOptions,
	GeneralInformation,
	LifeCycleResult,
	Step,
	LifeCycleResultGraph,
	MaterialCompositionResult,
	EmissionFactors,
	Unit,
} from "../../entities/NSwagGenerated"
import { useToast } from "primevue/usetoast"
import { library } from "@fortawesome/fontawesome-svg-core"
import { faInfoCircle, faTrash, faPen, faFloppyDisk, faXmarkLarge } from "@fortawesome/pro-regular-svg-icons"
import DynamicChartComponent, { ChartDataItem } from "./DynamicChartComponent.vue"
import ResultGridFilterBadge from "./ResultGridFilterBadge.vue"
import MaterialNameCellRenderer from "./MaterialNameCellRenderer.vue"

library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge)

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const mgmClient = new MgmRawMaterialsClient(import.meta.env.VITE_API_URL?.toString())

const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	activeEmissionFactors: {
		type: Array<EmissionFactors>,
		required: false,
		default: [],
	},
	calculationOptions: {
		type: CalculationOptions,
		required: false,
		default: undefined,
	},
	selectedStep: {
		type: Step,
		required: false,
		default: undefined,
	},
	selectedUnit: {
		type: Unit,
		required: false,
		default: undefined,
	},
	tabName: {
		type: String,
		required: false,
		default: "",
	},
	steps: {
		type: Array as () => Step[],
		required: false,
		default: () => [],
	},
})

import { useStepOutputDescription } from "@/composables/stepOutputDescription"
const outputDescription = useStepOutputDescription(
	computed(() => props.tabName),
	computed(() => props.selectedStep),
	computed(() => props.steps as Step[])
)

import { CellStyle, GridApi, GridReadyEvent, ValueGetterParams, ColDef } from "ag-grid-community"
const gridApi = ref<GridApi | null>(null)

const pinnedBottomRow = computed(() => {
	if (!visibleRows.value.length) return []

	const totals = {
		material: "Subtotal [Material Production]",
		climateChange: 0,
		waterConsumption: 0,
		water: 0,
		solvent: 0,
		total: 0,
		totalWeightedScore: 0,
		emissionFactors: {} as { [key: string]: number },
	}

	for (const row of visibleRows.value) {
		totals.climateChange += row.climateChange ?? 0
		totals.waterConsumption += row.waterConsumption ?? 0
		totals.water += row.water ?? 0
		totals.solvent += row.solvent ?? 0
		totals.total += row.total ?? 0
		totals.totalWeightedScore += row.totalWeightedScore ?? 0
		if (row.emissionFactors) {
			for (const ef of props.activeEmissionFactors) {
				if (!ef.emissionFactorName) continue
				totals.emissionFactors[ef.emissionFactorName] =
					(totals.emissionFactors[ef.emissionFactorName] || 0) + (row.emissionFactors[ef.emissionFactorName] || 0)
			}
		}
	}

	return [totals]
})

function onGridReady(params: GridReadyEvent) {
	gridApi.value = params.api
	refreshVisibleRows()
}

import { AgGridVue } from "ag-grid-vue3"
import {
	getConditionalFormatColor,
	valueToStringFormatter,
	resultGridTheme,
	resultGridDefaultColDef,
	textFilterColDef,
	numberFilterColDef,
	BuildMinMaxValues,
	ColumnMinMax,
	BuildMinMaxValuesFromEmissionFactors,
} from "../composables/resultGridHelper"
import { useGridFilterSync } from "../composables/useGridFilterSync"
import { generateChartColorsFromField } from "../composables/chartColorHelper"
const columnMinMax = ref<Array<ColumnMinMax>>([])

// Chart data mapping for DynamicChartComponent
const chartDataItems = computed<ChartDataItem[]>(() => {
	return visibleRows.value.map((item) => ({
		name: item.material || "",
		emissionFactors: item.emissionFactors || {},
	}))
})

const dqsLabels: Record<number, string> = { 1: "1 - High", 2: "2 - Medium", 3: "3 - Low" }
const dqsRatings = ref<DataQualityRating[]>([])

function getDqsColor(score: number): { bg: string; fg: string } | null {
	const rating = dqsRatings.value.find((r) => r.dataQualityRatingId === score.toString())
	if (!rating?.color) return null
	return { bg: rating.color, fg: rating.foregroundColor ?? "#000000" }
}

const agGridColumnDefs = computed(() => [
	{ field: "material", headerName: "Material", cellRenderer: MaterialNameCellRenderer, ...textFilterColDef },
	{ field: "materialType", headerName: "Material Type", ...textFilterColDef },
	{ field: "step", headerName: "Step", ...textFilterColDef },
	{ field: "mass", headerName: "Mass (kg)", valueFormatter: (p: any) => valueToStringFormatter(p.value), ...numberFilterColDef },
	...(props.calculationOptions?.useWeightedEmissionFactors
		? [
				{
					field: "totalWeightedScore",
					headerName: "Total Weighted Score [mPt]",
					valueGetter: (p: ValueGetterParams) => {
						return p.data?.totalWeightedScore ?? null
					},
					valueFormatter: (p: any) => valueToStringFormatter(p.value),
					aggFunc: "sum",
					...numberFilterColDef,
				},
			]
		: []),
	...props.activeEmissionFactors.map<ColDef>((ef) => ({
		field: ef.emissionFactorName,
		headerName: props.calculationOptions?.useWeightedEmissionFactors ? ef.emissionFactorName?.replace(/ \[.*\]$/, " [mPt]") : ef.emissionFactorName,
		valueGetter: (p) => {
			const value = ef.emissionFactorName == null || !p.data?.emissionFactors ? null : (p.data?.emissionFactors[ef.emissionFactorName] ?? null)
			return value
		},
		valueFormatter: (p) => valueToStringFormatter(p.value),
		cellStyle: (params): CellStyle | null => {
			// Don't apply conditional formatting to pinned rows
			if (params.node.rowPinned) {
				return { "text-align": "right" }
			}
			let minMax = columnMinMax.value.find((item) => item.columnName === ef.emissionFactorName)
			if (minMax) {
				return {
					"background-color": getConditionalFormatColor(params.value, minMax.min, minMax.max),
					"text-align": "right",
				}
			}
			return null
		},
		aggFunc: "sum",
		...numberFilterColDef,
	})),
	{
		field: "dataQualityScore",
		headerName: "Data Quality Score",
		valueFormatter: (p: any) => {
			const v = p.value as number
			return v ? (dqsLabels[v] ?? `${v}`) : ""
		},
		cellRenderer: (params: any) => {
			if (params.node.rowPinned || !params.value) return ""
			const v = params.value as number
			const label = dqsLabels[v] ?? `${v}`
			const c = getDqsColor(v)
			if (!c) return label
			return `<span style="display:inline-block; padding:2px 12px; border-radius:9999px; background-color:${c.bg}; color:${c.fg}; font-size:0.85rem; line-height:1.4;">${label}</span>`
		},
		cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" } as CellStyle,
		width: 160,
		...numberFilterColDef,
	} as ColDef,
])

function getResults(generalInfomationId: string) {
	barClimateChangeData.labels = []
	barClimateChangeData.datasets[0].data = []
	barWaterConsumptionData.labels = []
	barWaterConsumptionData.datasets[0].data = []
	barPMIData.labels = []
	barPMIData.datasets[0].data = []

	getResultsByMaterialComposition(generalInfomationId)
}

function impactCategoryChange(impactCategoryName: string) {
	displayLifeCycleResultClimateChangeGraph.value = false
	displayLifeCycleResultWaterConsumptionGraph.value = false
	displayLifeCycleResultPMIGraph.value = false

	barClimateChangeData.datasets[0].data = []
	barWaterConsumptionData.datasets[0].data = []
	barPMIData.datasets[0].data = []

	barClimateChangeData.datasets[0].backgroundColor = []
	barWaterConsumptionData.datasets[0].backgroundColor = []
	barPMIData.datasets[0].backgroundColor = []
	barClimateChangeData.labels = []
	barWaterConsumptionData.labels = []
	barPMIData.labels = []

	if (impactCategoryName == "Climate change") {
		displayLifeCycleResultClimateChangeGraph.value = true
		resultsByMaterialCompositionList.value.forEach((item) => {
			barClimateChangeData.labels.push(item.material || "")
			barClimateChangeData.datasets[0].data.push(item.climateChange || 0)
		})
		barClimateChangeData.datasets[0].backgroundColor = generateChartColorsFromField(resultsByMaterialCompositionList.value, "climateChange")
	} else if (impactCategoryName == "Freshwater use") {
		displayLifeCycleResultWaterConsumptionGraph.value = true
		resultsByMaterialCompositionList.value.forEach((item) => {
			barWaterConsumptionData.labels.push(item.material || "")
			barWaterConsumptionData.datasets[0].data.push(item.waterConsumption || 0)
		})
		barWaterConsumptionData.datasets[0].backgroundColor = generateChartColorsFromField(resultsByMaterialCompositionList.value, "waterConsumption")
	} else if (impactCategoryName == "PMI") {
		displayLifeCycleResultPMIGraph.value = true
		resultsByMaterialCompositionList.value.forEach((item) => {
			barPMIData.labels.push(item.material || "")
			barPMIData.datasets[0].data.push(item.total || 0)
		})
		barPMIData.datasets[0].backgroundColor = generateChartColorsFromField(resultsByMaterialCompositionList.value, "total")
	}
}

let toast = useToast()
const displayResultsByMaterialComposition = ref(false)
const displayLifeCycleResultListGraph1 = ref(false)
const displayLifeCycleResultClimateChangeGraph = ref(false)
const displayLifeCycleResultWaterConsumptionGraph = ref(false)
const displayLifeCycleResultPMIGraph = ref(false)
const selectedImpactCategory = ref("")

const resultsByMaterialCompositionList = ref([] as MaterialCompositionResult[])

// Rows currently visible in the grid after filtering - drives the charts and the grand total row
const { visibleRows, isFiltered, refreshVisibleRows, clearAllFilters } = useGridFilterSync<MaterialCompositionResult>(gridApi, resultsByMaterialCompositionList)

const showPieChart = ref(true)
const scrollMode = ref(false)
const extendMode = ref(false)
watch(() => chartDataItems.value.length, (newLen) => {
	scrollMode.value = newLen > 7
})
const impactCategoryList = ["Climate change", "Freshwater use", "PMI"]

const barClimateChangeData = {
	labels: [] as string[],
	datasets: [
		{
			label: "Climate Change [kg CO₂e]",
			backgroundColor: [] as string[],
			borderColor: "#1988E5",
			data: [] as number[],
		},
	],
}

const barWaterConsumptionData = {
	labels: [] as string[],
	datasets: [
		{
			label: "Freshwater use [m³]",
			backgroundColor: [] as string[],
			borderColor: "#1988E5",
			data: [] as number[],
		},
	],
}

const barPMIData = {
	labels: [] as string[],
	datasets: [
		{
			label: "PMI [kg]",
			backgroundColor: [] as string[],
			borderColor: "#1988E5",
			data: [] as number[],
		},
	],
}

const horizontalStackOptions = {
	indexAxis: "y",
	maintainAspectRatio: false,
	responsive: true,
	scales: {
		x: {
			beginAtZero: true,
		},
		y: {
			beginAtZero: true,
		},
	},
	plugins: {
		legend: {
			display: true,
			position: "top",
			labels: {
				boxWidth: 0,
				padding: 15,
			},
		},
	},
}

onMounted(async () => {
	try {
		dqsRatings.value = await mgmClient.mgmGetQualityRatingsColors()
	} catch (e) {
		console.error("Failed to load data quality ratings", e)
	}
})

function getResultsByMaterialComposition(generalInformationId: string) {
	if (generalInformationId != null) {
		apiClient.getBiologicsResultsByMaterialComposition(generalInformationId, props.calculationOptions).then(
			(res) => {
				//console.log("getResultsByMaterialComposition", res)
				if (res && res != null && res.length > 0) {
					displayResultsByMaterialComposition.value = true
					displayLifeCycleResultListGraph1.value = true
					resultsByMaterialCompositionList.value = res
					const emissionFactors = resultsByMaterialCompositionList.value.map((item) => item.emissionFactors ?? {})
					columnMinMax.value = BuildMinMaxValuesFromEmissionFactors(emissionFactors)
					// Set dropdown to empty - user must manually select
					selectedImpactCategory.value = ""
				}
			},
			(err) => {
				//67441
				//toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				if (err.status == 400) {
					toast.add({ severity: "error", summary: `Error: ${err.response.message}`, life: 5000 })
					console.error("Error Id:", err.response.errorId)
				} else {
					toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				}
			}
		)
	}
}

const getGridApi = () => {
	return gridApi.value
}

defineExpose({
	getResults,
	getGridApi,
	getResultData: () => resultsByMaterialCompositionList.value,
})
</script>
<style></style>
